import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertTriangle, BadgeCheck, Key, Smartphone } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import truncate from "@/lib/truncate";
import Link from "next/link";
import { formatTimeAgo } from "@/lib/format-time-ago";
import { Script } from "@/types/script";
import { buildImageLink, buildAvatarLink } from "@/lib/image";
import { formatNumber } from "@/lib/format-number";
import { Skeleton } from "@/components/ui/skeleton";

type ScriptPreviewProps = Script & {
  index: number;
};

export default function ScriptPreview({ ...props }: ScriptPreviewProps) {
  const { index, ...script } = props;
  const timeAgo = formatTimeAgo(script.created_at);
  const isPriority = index < 8;

  return (
    <TooltipProvider>
      <li className="flex w-full flex-col rounded-md border border-primary/5 bg-white/5 shadow-md transition hover:bg-white/10 hover:shadow-lg">
        <Link href={`/script/${script.slug}`}>
          <AspectRatio ratio={16 / 9} className="bg-background/30">
            <div className="relative h-full w-full">
              <Image
                src={buildImageLink(script.images[0]!) ?? "/assets/cube.webp"}
                alt={`${truncate({
                  text: script.title,
                  length: 50,
                })} Script preview`}
                className="rounded-t-md border-l border-r border-t border-primary/5 object-cover"
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                quality={75}
                priority={isPriority}
                loading={isPriority ? "eager" : "lazy"}
              />
            </div>
            <div className="absolute bottom-0 right-0 z-10 w-full">
              <span className="flex flex-row items-center justify-end gap-1 p-2">
                {script.visibility === "Public" && (
                  <Tooltip>
                    <TooltipTrigger className="flex justify-center">
                      <Badge
                        variant="accent"
                        className="bg-background/50 text-white backdrop-blur hover:bg-background/75"
                      >
                        <CircleCheck /> Verified
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Verified script</TooltipContent>
                  </Tooltip>
                )}
                {script.mobile && (
                  <Tooltip>
                    <TooltipTrigger
                      className="flex justify-center"
                      aria-label="Mobile compatible"
                    >
                      <Badge
                        variant="accent"
                        className="bg-background/50 text-white backdrop-blur hover:bg-background/75"
                      >
                        <Smartphone size={14} />
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Mobile compatible</TooltipContent>
                  </Tooltip>
                )}
                {script.key_system && (
                  <Tooltip>
                    <TooltipTrigger
                      className="flex justify-center"
                      aria-label="Has a key system"
                    >
                      <Badge
                        variant="accent"
                        className="bg-background/50 text-white backdrop-blur hover:bg-background/75"
                      >
                        <Key size={14} />
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Has a key system</TooltipContent>
                  </Tooltip>
                )}
              </span>
            </div>
          </AspectRatio>
        </Link>
        <div className="flex flex-row items-start gap-2 p-4">
          <Avatar>
            <AvatarImage
              src={buildAvatarLink(script.user.username)}
              alt="Author avatar"
            />
            <AvatarFallback>{script.user?.username[0]}</AvatarFallback>
          </Avatar>
          <div className="flex flex-1 flex-col">
            <h3 className="font-heading break-words font-medium leading-tight">
              {truncate({
                text: script.title,
                length: 50,
              })}
            </h3>
            <span className="flex flex-col break-words text-sm text-muted-foreground">
              <span className="flex flex-row items-center gap-0.5">
                <Link
                  href={`/user/${script.user.username}`}
                  className="transition hover:text-foreground"
                >
                  @{script.user.username}
                </Link>
                {script.user.role != "User" && (
                  <Tooltip>
                    <TooltipTrigger
                      className="flex justify-center"
                      aria-label="Verified author"
                    >
                      <BadgeCheck className="size-4 fill-primary text-card" />
                    </TooltipTrigger>
                    <TooltipContent>Verified author</TooltipContent>
                  </Tooltip>
                )}
              </span>
              <span>
                {formatNumber(script.views)}{" "}
                {script.views === 1 ? "view" : "views"} • {timeAgo}
              </span>
            </span>
          </div>
        </div>
      </li>
    </TooltipProvider>
  );
}

export function CircleCheck() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      className="inline-flex h-3 w-3"
    >
      <path
        fill="currentColor"
        d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"
      />
    </svg>
  );
}

export function ScriptPreviewSkeleton() {
  return (
    <li className="flex w-full flex-col rounded-md border border-primary/5 bg-white/5 shadow-md">
      <AspectRatio ratio={16 / 9}>
        <Skeleton className="absolute inset-0 h-full w-full rounded-t-md border-l border-r border-t border-primary/5" />
      </AspectRatio>
      <div className="flex flex-row items-start gap-2 p-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
    </li>
  );
}
